import React, { useState, useEffect } from "react";
import {
  Wallet, ArrowUpRight, Clock, DollarSign, Calendar,
  Download, FileText, Filter, Search, Building2, Plus,
  CheckCircle, AlertCircle, X, ArrowRight, User, Mail,
  CreditCard, ChevronDown, MoreVertical,
  BanknoteIcon, RefreshCw
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/Button";
import { Badge } from "../../components/ui/badge";
import AdminHeader from "../../Headers/AdminHeader";
import SharedFooter3 from "../../Footer/SharedFooter3";
import { apiService } from "../../services/paymentAPI";
import { Withdrawal, WithdrawalStatus, WithdrawalStatusLabels } from "../../types/payment";
import { GlobalService } from "../../modules";
import WithdrawalReceiptDialog from "../../components/payment/WithdrawalReceiptDialog";
import PayoutNotificationService from "../../services/payoutNotificationService";
import { useSelector } from "react-redux";

const PayoutsPage = () => {
  // Get admin user from redux
  const { user: adminUser } = useSelector((state: any) => state.auth);
  
  // Local state management
  const [withdrawals, setWithdrawals] = useState<Withdrawal[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState<number | "all">("all");
  const [showPayoutDetails, setShowPayoutDetails] = useState<Withdrawal | null>(null);
  const [dateRange, setDateRange] = useState("all");
  const [sortBy, setSortBy] = useState("newest");
  const [page, setPage] = useState(1);
  const [showReceiptDialog, setShowReceiptDialog] = useState(false);
  const [selectedWithdrawalForReceipt, setSelectedWithdrawalForReceipt] = useState<Withdrawal | null>(null);
  
  // Stats state
  const [totalPending, setTotalPending] = useState(0);
  const [totalProcessing, setTotalProcessing] = useState(0);
  const [totalCompleted, setTotalCompleted] = useState(0);
  const [totalFailed, setTotalFailed] = useState(0);

  // Calculate date range based on selection
  const getDateRange = () => {
    const now = new Date();
    let startDate = "";
    let endDate = now.toISOString();

    switch (dateRange) {
      case "week": {
        const weekAgo = new Date(now);
        weekAgo.setDate(weekAgo.getDate() - 7);
        startDate = weekAgo.toISOString();
        break;
      }
      case "month": {
        const monthAgo = new Date(now);
        monthAgo.setMonth(monthAgo.getMonth() - 1);
        startDate = monthAgo.toISOString();
        break;
      }
      case "quarter": {
        const quarterAgo = new Date(now);
        quarterAgo.setMonth(quarterAgo.getMonth() - 3);
        startDate = quarterAgo.toISOString();
        break;
      }
      case "year": {
        const yearAgo = new Date(now);
        yearAgo.setFullYear(yearAgo.getFullYear() - 1);
        startDate = yearAgo.toISOString();
        break;
      }
      case "all":
      default:
        startDate = "";
        endDate = "";
        break;
    }

    return { startDate, endDate };
  };

  // Get sort options based on selection
  const getSortOptions = () => {
    switch (sortBy) {
      case "newest":
        return [{ orderBy: "createdAt", order: "DESC" }];
      case "oldest":
        return [{ orderBy: "createdAt", order: "ASC" }];
      case "amount_high":
        return [{ orderBy: "amount", order: "DESC" }];
      case "amount_low":
        return [{ orderBy: "amount", order: "ASC" }];
      default:
        return [{ orderBy: "createdAt", order: "DESC" }];
    }
  };

  // Fetch withdrawals function
  const fetchWithdrawals = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: any = {};

      if (selectedStatus !== "all") {
        filters.status = selectedStatus;
      }

      if (selectedMethod !== "all") {
        filters.withdrawalMethod = selectedMethod;
      }

      // Add date range filters
      const { startDate, endDate } = getDateRange();
      if (startDate) {
        filters.startDate = startDate;
      }
      if (endDate) {
        filters.endDate = endDate;
      }

      // Get sort options
      const sortOptions = getSortOptions();

      const response = await apiService.getWithdrawals(page, 10, filters, sortOptions);

      const data = response.data || [];
      
      // Filter by search query on the frontend (if backend doesn't support it)
      let filteredData = data;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filteredData = data.filter((w: Withdrawal) => 
          w.id.toLowerCase().includes(query) ||
          w.user?.email?.toLowerCase().includes(query) ||
          w.user?.firstName?.toLowerCase().includes(query) ||
          w.user?.lastName?.toLowerCase().includes(query) ||
          w.withdrawalMethod?.toLowerCase().includes(query)
        );
      }

      setWithdrawals(filteredData);
      setTotalCount(response.totalCount || data.length);

      // Calculate stats from all data (not just filtered)
      setTotalPending(data.filter((w: Withdrawal) => w.status === WithdrawalStatus.PENDING).reduce((sum: number, w: Withdrawal) => sum + (w.amount || 0), 0));
      setTotalProcessing(data.filter((w: Withdrawal) => w.status === WithdrawalStatus.PROCESSING).reduce((sum: number, w: Withdrawal) => sum + (w.amount || 0), 0));
      setTotalCompleted(data.filter((w: Withdrawal) => w.status === WithdrawalStatus.COMPLETED).reduce((sum: number, w: Withdrawal) => sum + (w.amount || 0), 0));
      setTotalFailed(data.filter((w: Withdrawal) => w.status === WithdrawalStatus.FAILED).reduce((sum: number, w: Withdrawal) => sum + (w.amount || 0), 0));
    } catch (err: any) {
      setError(err?.message || "Failed to fetch withdrawals");
      GlobalService.showToast("error", "Error", err?.message || "Failed to fetch withdrawals");
    } finally {
      setLoading(false);
    }
  };

  // Open receipt dialog function
  const openReceiptDialog = (withdrawal: Withdrawal) => {
    setSelectedWithdrawalForReceipt(withdrawal);
    setShowReceiptDialog(true);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
  }, [selectedStatus, selectedMethod, searchQuery, dateRange, sortBy]);

  // Fetch withdrawals on component mount and when filters/page change
  useEffect(() => {
    fetchWithdrawals();
  }, [page, selectedStatus, selectedMethod, sortBy, dateRange]);

  // Helper functions for status handling
  const getStatusBadgeClass = (status: number | null | undefined): string => {
    switch (status) {
      case WithdrawalStatus.PENDING:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case WithdrawalStatus.PROCESSING:
        return "bg-blue-100 text-blue-800 border-blue-200";
      case WithdrawalStatus.COMPLETED:
        return "bg-green-100 text-green-800 border-green-200";
      case WithdrawalStatus.FAILED:
        return "bg-red-100 text-red-800 border-red-200";
      case WithdrawalStatus.CANCELLED:
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  const getStatusText = (status: number | null | undefined): string => {
    return WithdrawalStatusLabels[status as WithdrawalStatus] || "Unknown";
  };

  const formatAmount = (amount: number | null | undefined, currency: string | null | undefined): string => {
    const amt = amount || 0;
    const curr = currency || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: curr.toUpperCase(),
    }).format(amt);
  };

  const getMethodDisplay = (withdrawal: Withdrawal): string => {
    if (withdrawal.withdrawalMethod === "stripe") return "Stripe Connect";
    if (withdrawal.withdrawalMethod === "paypal") return `PayPal - ${withdrawal.paypalEmail || "N/A"}`;
    if (withdrawal.withdrawalMethod === "bank_account") return "Bank Account";
    return "N/A";
  };

  // Payout Details Modal Component
  const PayoutDetailsModal = ({ payout, isOpen, onClose }: { payout: Withdrawal | null; isOpen: boolean; onClose: () => void }) => {
    const [bankAccountDetails, setBankAccountDetails] = useState<any>(null);
    const [loadingBankDetails, setLoadingBankDetails] = useState(false);

    // Fetch bank account details when modal opens with bank_account method
    useEffect(() => {
      const fetchBankAccountDetails = async () => {
        if (payout?.withdrawalMethod === "bank_account" && payout?.bankAccountId) {
          try {
            setLoadingBankDetails(true);
            const response = await apiService.getPaymentMethodById(payout.bankAccountId);
            setBankAccountDetails(response);
          } catch (err: any) {
            console.error("Failed to fetch bank account details:", err);
            GlobalService.showToast("error", "Error", "Failed to load bank account details");
          } finally {
            setLoadingBankDetails(false);
          }
        }
      };

      if (isOpen && payout) {
        fetchBankAccountDetails();
      } else {
        setBankAccountDetails(null);
      }
    }, [isOpen, payout]);

    if (!payout || !isOpen) return null;

    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.95 }}
            animate={{ scale: 1 }}
            exit={{ scale: 0.95 }}
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          >
            {/* Header */}
            <div className="p-6 border-b" style={{ textAlign: "left" }}>
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-3">
                    <Badge variant={undefined} className={getStatusBadgeClass(payout.status)}>
                      {getStatusText(payout.status).toUpperCase()}
                    </Badge>
                    <Badge variant="outline" className="border-purple-200 text-purple-700">
                      {payout.withdrawalMethod?.toUpperCase() || "N/A"}
                    </Badge>
                  </div>
                  <h2 className="text-xl font-semibold mt-2">Withdrawal #{payout.id.substring(0, 8)}</h2>
                  <p className="text-sm text-gray-500">Created on {new Date(payout.createdAt).toLocaleString()}</p>
                </div>
                <button
                  onClick={onClose}
                  className="p-2 hover:bg-gray-100 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-6 space-y-6">
              {/* Amount Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Withdrawal Amount</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-sm text-gray-500">Amount</span>
                      <p className="text-2xl font-bold text-gray-900">
                        {formatAmount(payout.amount, payout.currency)}
                      </p>
                    </div>
                    <div>
                      <span className="text-sm text-gray-500">Status</span>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant={undefined} className={getStatusBadgeClass(payout.status)}>
                          {getStatusText(payout.status)}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* User Details */}
              <div className="space-y-4">
                <h3 className="font-medium">User Details</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="h-12 w-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">{payout.user?.firstName} {payout.user?.lastName}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Mail className="h-4 w-4 text-gray-400" />
                        <span className="text-sm text-gray-600">{payout.user?.email || "N/A"}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Payment Method Information */}
              <div className="space-y-4">
                <h3 className="font-medium">Payment Method</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-3 mb-4">
                    <BanknoteIcon className="h-5 w-5 text-gray-500" />
                    <span className="font-medium">{getMethodDisplay(payout)}</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    {payout.withdrawalMethod === "stripe" && (
                      <>
                        <div>
                          <span className="text-sm text-gray-500">Transfer ID</span>
                          <p className="font-medium text-xs break-all">{payout.stripeTransferId || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Payout ID</span>
                          <p className="font-medium text-xs break-all">{payout.stripePayoutId || "N/A"}</p>
                        </div>
                      </>
                    )}
                    {payout.withdrawalMethod === "paypal" && (
                      <>
                        <div>
                          <span className="text-sm text-gray-500">PayPal Email</span>
                          <p className="font-medium">{payout.paypalEmail || "N/A"}</p>
                        </div>
                        <div>
                          <span className="text-sm text-gray-500">Batch ID</span>
                          <p className="font-medium text-xs break-all">{payout.paypalPayoutBatchId || "N/A"}</p>
                        </div>
                      </>
                    )}
                    {payout.withdrawalMethod === "bank_account" && (
                      <>
                        {loadingBankDetails ? (
                          <div className="col-span-2">
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              <span className="text-sm text-gray-500">Loading bank details...</span>
                            </div>
                          </div>
                        ) : bankAccountDetails ? (
                          <>
                            <div>
                              <span className="text-sm text-gray-500">Account Holder</span>
                              <p className="font-medium">{bankAccountDetails.accountHolderName || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Bank Name</span>
                              <p className="font-medium">{bankAccountDetails.bankName || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">IBAN</span>
                              <p className="font-medium text-xs break-all">{bankAccountDetails.iban || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">BIC/SWIFT</span>
                              <p className="font-medium">{bankAccountDetails.bic || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Account Type</span>
                              <p className="font-medium">{bankAccountDetails.accountType || "N/A"}</p>
                            </div>
                            <div>
                              <span className="text-sm text-gray-500">Type</span>
                              <p className="font-medium">{bankAccountDetails.type || "N/A"}</p>
                            </div>
                          </>
                        ) : (
                          <div className="col-span-2">
                            <span className="text-sm text-gray-500">Bank Account ID</span>
                            <p className="font-medium text-xs break-all">{payout.bankAccountId || "N/A"}</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Timeline */}
              <div className="space-y-4">
                <h3 className="font-medium">Timeline</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Created</span>
                      <span className="font-medium">{new Date(payout.createdAt).toLocaleString()}</span>
                    </div>
                    {payout.processedAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Processed</span>
                        <span className="font-medium">{new Date(payout.processedAt).toLocaleString()}</span>
                      </div>
                    )}
                    {payout.completedAt && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Completed</span>
                        <span className="font-medium">{new Date(payout.completedAt).toLocaleString()}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Notes and Failure Reason */}
              {(payout.notes || payout.failureReason) && (
                <div className="space-y-4">
                  <h3 className="font-medium">Additional Information</h3>
                  <div className="bg-gray-50 rounded-lg p-4">
                    {payout.notes && (
                      <div className="mb-3">
                        <span className="text-sm font-medium text-gray-700">Notes:</span>
                        <p className="text-sm text-gray-600 mt-1 whitespace-pre-wrap">{payout.notes}</p>
                      </div>
                    )}
                    {payout.failureReason && (
                      <div className="p-3 bg-red-50 border border-red-200 rounded">
                        <span className="text-sm font-medium text-red-800">Failure Reason:</span>
                        <p className="text-sm text-red-700 mt-1">{payout.failureReason}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  variant="outline"
                  onClick={onClose}
                  className="flex items-center gap-2"
                >
                  Close
                </Button>
                <Button
                  variant="outline"
                  onClick={() => openReceiptDialog(payout)}
                  className="flex items-center gap-2"
                >
                  <Download className="h-4 w-4" />
                  Download Receipt
                </Button>
                {payout.status === WithdrawalStatus.PENDING && (
                  <Button 
                    onClick={async () => {
                      try {
                        await apiService.markWithdrawalAsProcessing(payout.id);
                        GlobalService.showToast("success", "Success", "Withdrawal marked as processing");
                        
                        // Send notification to user
                        await PayoutNotificationService.sendWithdrawalNotification(
                          payout,
                          "processing",
                          adminUser?.id || "admin"
                        );
                        
                        fetchWithdrawals(); // Refresh list
                        onClose();
                      } catch (err: any) {
                        GlobalService.showToast("error", "Error", err?.response?.data?.message || "Failed to update status");
                      }
                    }}
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Mark as Processing
                  </Button>
                )}
                {payout.status === WithdrawalStatus.PROCESSING && (
                  <Button 
                    onClick={async () => {
                      try {
                        await apiService.markWithdrawalAsCompleted(payout.id);
                        GlobalService.showToast("success", "Success", "Withdrawal marked as completed");
                        
                        // Send notification to user
                        await PayoutNotificationService.sendWithdrawalNotification(
                          payout,
                          "completed",
                          adminUser?.id || "admin"
                        );
                        
                        fetchWithdrawals(); // Refresh list
                        onClose();
                      } catch (err: any) {
                        GlobalService.showToast("error", "Error", err?.response?.data?.message || "Failed to update status");
                      }
                    }}
                    className="flex items-center gap-2 bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4" />
                    Mark as Completed
                  </Button>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>
    );
  };


  return (
    <div className="min-h-screen bg-gray-50" style={{ textAlign: "left" }}>
      <AdminHeader />

      <main className="flex-1 lg:pl-64 pt-16">
        <div className="p-6">
          <div className="max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payouts</h1>
                <p className="text-gray-600 mt-1">Manage and process professional payouts</p>
                {error && (
                  <div className="mt-2 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
                    {error}
                    <button
                      onClick={() => setError(null)}
                      className="ml-2 text-red-500 hover:text-red-700"
                    >
                      ×
                    </button>
                  </div>
                )}
              </div>
              <div className="flex gap-3">
                <Button 
                  variant="outline" 
                  onClick={() => fetchWithdrawals()}
                  className="flex items-center gap-2"
                >
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </Button>
                <Button variant="outline" className="flex items-center gap-2">
                  <Download className="h-4 w-4" />
                  Export
                </Button>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Pending</p>
                    <p className="text-2xl font-semibold">${totalPending.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-2 bg-yellow-100 rounded-lg">
                    <Clock className="h-5 w-5 text-yellow-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Processing</p>
                    <p className="text-2xl font-semibold">${totalProcessing.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <ArrowUpRight className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Completed</p>
                    <p className="text-2xl font-semibold">${totalCompleted.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-2 bg-green-100 rounded-lg">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                </div>
              </Card>

              <Card className="p-6">
                <div className="flex justify-between items-start">
                  <div className="space-y-1">
                    <p className="text-sm text-gray-500">Failed</p>
                    <p className="text-2xl font-semibold">${totalFailed.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</p>
                  </div>
                  <div className="p-2 bg-red-100 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </Card>
            </div>

            {/* Search and Filters */}
            <Card className="p-4">
              <div className="flex flex-col lg:flex-row justify-between gap-4">
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 w-full lg:w-auto">
                  <div className="relative flex-1 w-full sm:w-[300px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search withdrawals..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          fetchWithdrawals();
                        }
                      }}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg"
                    />
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => fetchWithdrawals()}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Search className="h-5 w-5" />
                    Search
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 w-full sm:w-auto justify-center"
                  >
                    <Filter className="h-5 w-5" />
                    Filters
                  </Button>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch gap-4 w-full lg:w-auto">
                  <select
                    value={dateRange}
                    onChange={(e) => setDateRange(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="all">All Time</option>
                    <option value="week">This Week</option>
                    <option value="month">This Month</option>
                    <option value="quarter">This Quarter</option>
                    <option value="year">This Year</option>
                  </select>

                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    className="px-4 py-2 border rounded-lg w-full sm:w-auto"
                  >
                    <option value="newest">Newest First</option>
                    <option value="oldest">Oldest First</option>
                    <option value="amount_high">Highest Amount</option>
                    <option value="amount_low">Lowest Amount</option>
                  </select>
                </div>
              </div>

              {/* Advanced Filters */}
              {showFilters && (
                <div className="mt-4 pt-4 border-t">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Withdrawal Method
                      </label>
                      <select
                        value={selectedMethod}
                        onChange={(e) => setSelectedMethod(e.target.value)}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Methods</option>
                        <option value="stripe">Stripe Connect</option>
                        <option value="paypal">PayPal</option>
                        <option value="bank_account">Bank Account</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Status
                      </label>
                      <select
                        value={selectedStatus}
                        onChange={(e) => setSelectedStatus(e.target.value === "all" ? "all" : parseInt(e.target.value))}
                        className="w-full px-4 py-2 border rounded-lg"
                      >
                        <option value="all">All Status</option>
                        <option value={WithdrawalStatus.PENDING}>Pending</option>
                        <option value={WithdrawalStatus.PROCESSING}>Processing</option>
                        <option value={WithdrawalStatus.COMPLETED}>Completed</option>
                        <option value={WithdrawalStatus.FAILED}>Failed</option>
                        <option value={WithdrawalStatus.CANCELLED}>Cancelled</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}
            </Card>

            {/* Withdrawals Table */}
            <Card className="overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Withdrawal ID
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Amount
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Method
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Date
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {loading ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center">
                          <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span className="ml-2 text-gray-500">Loading withdrawals...</span>
                          </div>
                        </td>
                      </tr>
                    ) : withdrawals.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="px-6 py-8 text-center text-gray-500">
                          No withdrawals found
                        </td>
                      </tr>
                    ) : (
                      withdrawals.map((withdrawal) => (
                        <tr key={withdrawal.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="font-medium text-gray-900 text-xs">#{withdrawal.id.substring(0, 8)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div>
                                <div className="font-medium text-gray-900 text-sm">
                                  {withdrawal.user?.firstName} {withdrawal.user?.lastName}
                                </div>
                                <div className="text-xs text-gray-500">{withdrawal.user?.email || "N/A"}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm font-medium text-gray-900">
                              {formatAmount(withdrawal.amount, withdrawal.currency)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant="outline" className="border-purple-200 text-purple-700">
                              {withdrawal.withdrawalMethod?.toUpperCase() || "N/A"}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <Badge variant={undefined} className={getStatusBadgeClass(withdrawal.status)}>
                              {getStatusText(withdrawal.status).toUpperCase()}
                            </Badge>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(withdrawal.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <div className="flex items-center gap-2 justify-end">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openReceiptDialog(withdrawal)}
                                className="text-green-600 hover:text-green-900 flex items-center gap-1"
                              >
                                <Download className="h-3 w-3" />
                                <span className="text-xs">Receipt</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowPayoutDetails(withdrawal)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                View Details
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="px-6 py-4 border-t flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-700">
                    Showing page {page} of {Math.ceil(totalCount / 10) || 1}
                  </span>
                  <span className="text-sm text-gray-500">
                    ({totalCount} total withdrawals)
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1 || loading}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(totalCount / 10) || loading}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </main>

      {/* Modals */}
      <PayoutDetailsModal
        payout={showPayoutDetails}
        isOpen={!!showPayoutDetails}
        onClose={() => setShowPayoutDetails(null)}
      />

      {/* Receipt Dialog */}
      <WithdrawalReceiptDialog
        isOpen={showReceiptDialog}
        onClose={() => {
          setShowReceiptDialog(false);
          setSelectedWithdrawalForReceipt(null);
        }}
        withdrawal={selectedWithdrawalForReceipt}
      />

      {/* <SharedFooter3 /> */}
    </div>
  );
};

export default PayoutsPage;